import {
	AsyncSelect,
	AsyncSelectProps,
	Box,
	Icons,
	Table,
	Dropdown,
	joinAttributes,
	Stack,
	Text,
	Inline,
} from "@sembark-travel/ui-base"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import {
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import React, { Fragment, useCallback, useEffect, useState } from "react"
import { Omit } from "utility-types"
import { Email, PhoneNumber } from "../Contacts"
import { AddTransportServiceProviderDialog } from "./NewItem"
import { ISupplier, TSupplyType } from "./store"
import { UpdateItemInDialog } from "./UpdateItem"
import { SwitchInputField } from "@sembark-travel/ui-form"
import { PERMISSIONS, useCheckPermissions, useHasFeatureFlag } from "../Auth"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { generatePath } from "../router-utils"

function XHR(xhr: XHRInstance) {
	return {
		async get(params?: unknown): Promise<IListResponse<ISupplier>> {
			return xhr
				.get("/transport-service-providers", { params })
				.then((resp) => resp.data)
		},
		async restoreSupplier(id: number | string) {
			return xhr
				.patch<{
					message: string
				}>(`/transport-service-providers/${id}/restore`)
				.then((res) => res.data)
		},

		async destroySupplier(id: number | string) {
			return xhr
				.delete<{
					message: string
				}>(`/transport-service-providers/${id}/delete`)
				.then((res) => res.data)
		},

		async disableSupplier(id: number | string) {
			return xhr
				.delete<{
					message: string
				}>(`/transport-service-providers/${id}/disable`)
				.then((res) => res.data)
		},
	}
}

type TFilters = TSearchParams & {
	disabled_only?: 1 | 0
}

export function SupplierList({
	title,
	supplierType,
}: Readonly<{
	title: string
	supplierType: TSupplyType
}>) {
	const { hasPermission, hasAnyPermission } = useCheckPermissions()
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch<TFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const hasTravelActivities = useHasFeatureFlag("travel_activities")
	return (
		<Search
			initialParams={params}
			onSearch={(params) => {
				setParams({ ...params, page: 1 })
			}}
			title={title}
			actions={
				<Inline gap={"2"}>
					{hasAnyPermission(
						PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS,
						PERMISSIONS.VIEW_HOTEL_SUPPLIERS
					) ? (
						<ButtonLink
							to={generatePath("/suppliers/new")}
							level="primary"
							status="primary"
							query={{ type: supplierType }}
							anchored
						>
							<Icons.Plus /> Add Supplier
						</ButtonLink>
					) : null}
					{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
						<ButtonLink to={generatePath("/suppliers/merge")} anchored>
							Merge Suppliers
						</ButtonLink>
					) : null}
				</Inline>
			}
			Filters={Filters}
		>
			<ListView<ISupplier, TFilters>
				pageKey={`suppliers/${supplierType}`}
				params={params}
				fetch={(xhr, params) =>
					XHR(xhr).get({
						...params,
						include: "contacts",
						// convert to the request params
						supplies: supplierType,
					})
				}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items: suppliers, refresh, xhr }) => (
					<Table
						responsive
						bordered
						hover
						headers={["Name", "Contacts", ""]}
						alignCols={{ 2: "right" }}
						rows={suppliers.map((supplier) => [
							<Link
								to={generatePath(`/suppliers/:supplierId`, {
									supplierId: supplier.id.toString(),
								})}
								state={{ params }}
								color="accent"
								fontWeight="semibold"
								anchored
							>
								<Inline gap="4" flexWrap="wrap">
									<Text>{supplier.name}</Text>
									<Text as="span" color="muted">
										{joinAttributes(
											<Box display="inlineBlock">
												{supplier.solo_driver ? (
													<Icons.Driver title="Single Driver" color="muted" />
												) : (
													<Icons.UserGroup title="Company" color="muted" />
												)}
											</Box>,
											supplier.supplies_cabs && hasTravelActivities ? (
												<Icons.Taxi title="Cab Supplier" />
											) : null,
											supplier.supplies_travel_activities ? (
												<Icons.Ticket title="Travel Activity Supplier" />
											) : null,
											supplier.supplies_hotels ? (
												<Icons.Bed title="Hotel Supplier" />
											) : null,
											supplier.supplies_visas ? (
												<Icons.Visa title="Visa Supplier" />
											) : null
										)}
									</Text>
								</Inline>
							</Link>,
							<Box>
								{supplier.contacts?.length ? (
									<Box>
										{joinAttributes(
											supplier.contacts[0].name,
											supplier.contacts[0].phone_numbers?.length ? (
												<PhoneNumber
													value={supplier.contacts[0].phone_numbers}
													iconOnly
												/>
											) : null,
											supplier.contacts[0].email ? (
												<Email value={supplier.contacts[0].email} iconOnly />
											) : null
										)}
									</Box>
								) : null}
							</Box>,
							<Box>
								<Dropdown alignRight>
									<Dropdown.ToggleButton size="sm" level="tertiary">
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										{supplier.disabled_at ? (
											<Dropdown.MenuItem
												onClick={() => {
													if (
														window.confirm(
															"Are you sure you want to enable this supplier ?"
														)
													) {
														XHR(xhr)
															.restoreSupplier(supplier.id)
															.then((resp) => {
																showSnackbar(
																	resp.message ||
																		"Supplier enabled successfully."
																)
																refresh()
															})
															.catch((e) => {
																const error: Error = e
																window.alert(
																	error.message || "Something went wrong"
																)
															})
													}
												}}
											>
												<Icons.Refresh /> Enable
											</Dropdown.MenuItem>
										) : (
											<>
												<UpdateItemInDialog
													supplier={supplier}
													onSuccess={() => refresh()}
												>
													{({ edit }) => (
														<Dropdown.MenuItem onClick={() => edit()}>
															<Icons.Pencil /> Edit Details
														</Dropdown.MenuItem>
													)}
												</UpdateItemInDialog>
												<Dropdown.MenuItem
													onClick={() => {
														if (
															window.confirm(
																"This supplier will be removed from all supplier listing and selection pages. Existing usage of this supplier will not be affected.\n Are you sure you want to disable this supplier ?"
															)
														) {
															XHR(xhr)
																.disableSupplier(supplier.id)
																.then((resp) => {
																	showSnackbar(
																		resp.message ||
																			"Supplier disabled successfully."
																	)
																	refresh()
																})
																.catch((e) => {
																	const error: Error = e
																	window.alert(
																		error.message || "Something went wrong"
																	)
																})
														}
													}}
												>
													<Icons.Ban /> Disable
												</Dropdown.MenuItem>
											</>
										)}

										<Dropdown.MenuItem
											onClick={() => {
												if (
													window.confirm(
														"Are you sure you want to delete this supplier ?"
													)
												) {
													XHR(xhr)
														.destroySupplier(supplier.id)
														.then((resp) => {
															showSnackbar(
																resp.message ||
																	"Supplier  deleted successfully."
															)
															refresh()
														})
														.catch((e) => {
															const error: Error = e
															window.alert(
																error.message || "Something went wrong"
															)
														})
												}
											}}
										>
											<Icons.Trash /> Delete
										</Dropdown.MenuItem>
									</Dropdown.Menu>
								</Dropdown>
							</Box>,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

function Filters() {
	return (
		<Stack>
			<SwitchInputField name="disabled_only" label="Disabled Only" />
		</Stack>
	)
}

export function SelectServiceProviders({
	supplies,
	visaCountries,
	...otherProps
}: Omit<AsyncSelectProps, "fetch"> & {
	supplies?: TSupplyType | Array<TSupplyType>
	visaCountries?: number[]
}) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	const handleDialogClose = useCallback(() => {
		setNewName("")
	}, [setNewName])
	return (
		<Fragment>
			<AsyncSelect
				onCreateNew={(query: string) => {
					setNewName(query)
				}}
				optionRenderer={({ option, created }) =>
					created ? <Box>Add "{option.name}"</Box> : <Box>{option.name}</Box>
				}
				{...otherProps}
				perFetchLimit={15}
				fetch={(q, { page }) =>
					XHR(xhr)
						.get({
							q,
							include:
								"drivers,cabs,supplier_payment_preferences,contacts,accounting_accounts",
							limit: 15,
							pagination: "simple",
							page,
							supplies,
							visa_countries:
								visaCountries && visaCountries.length > 0
									? visaCountries
									: undefined,
						})
						.then((resp) => resp.data)
				}
			/>
			<AddTransportServiceProviderDialog
				open={!!newName}
				initialValues={{
					name: newName,
					supplies_cabs: supplies?.indexOf("cabs") !== -1 ? 1 : 0,
					supplies_travel_activities:
						supplies?.indexOf("travel_activities") !== -1 ? 1 : 0,
					supplies_hotels: supplies?.indexOf("hotels") !== -1 ? 1 : 0,
				}}
				keepInitialSuppliesSelected
				onClose={handleDialogClose}
				onSuccess={(tsp) => {
					handleDialogClose()
					otherProps.onChange?.(tsp, otherProps.name || "field_name")
				}}
			/>
		</Fragment>
	)
}

export function SelectTransporServiceProviders(
	props: Omit<React.ComponentProps<typeof SelectServiceProviders>, "supplies">
) {
	return <SelectServiceProviders {...props} supplies={"cabs"} />
}
export function SelectTransportAndActivitySuppliers(
	props: Omit<React.ComponentProps<typeof SelectServiceProviders>, "supplies">
) {
	return (
		<SelectServiceProviders
			{...props}
			supplies={["cabs", "travel_activities", "hotels"]}
		/>
	)
}

export function SelectTravelActivitySuppliers(
	props: Omit<React.ComponentProps<typeof SelectServiceProviders>, "supplies">
) {
	return <SelectServiceProviders {...props} supplies={"travel_activities"} />
}
export function SelectHotelSuppliers(
	props: Omit<React.ComponentProps<typeof SelectServiceProviders>, "supplies">
) {
	return <SelectServiceProviders {...props} supplies={"hotels"} />
}

export function SelectVisaSuppliers(
	props: Omit<React.ComponentProps<typeof SelectServiceProviders>, "supplies">
) {
	return <SelectServiceProviders {...props} supplies={"visas"} />
}
